import { CommonModule, I18nPluralPipe, NgOptimizedImage } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { Bildungsangebot } from '../../model';
import { ScrollToAndFocusDirective } from '../../shared/directives';
import { ANBIETER_LOGO_FALLBACK } from '../../shared/utils/anbieter-logo-fallback';
import { getLabelFromWeiterbildungsart } from '../../model/weiterbildungsarten';
import { breakTitle } from '../../shared/utils/break-title';
import { SanitizerService } from '../../services/sanitizer.service';
import { TruncatePipe } from '../../shared/pipes/truncate/truncate.pipe';
import { htmlZuText } from '../../shared/utils/html-zu-text';
import { TerminTabelleKachelComponent } from '../angebot-termine-kachel/termin-tabelle-kachel/termin-tabelle-kachel.component';
import { LiveAnnouncer } from '../../services/a11y';
import { LIB_CONFIG } from '../../lib.config';
import { CommonStateService } from '../../services/state/common-state.service';
import {
  FavoriteToggleComponent,
  Weiterbildungsangebot
} from '../detail/favorite-toggle/favorite-toggle.component';

@Component({
  selector: 'now-wbsuche-lib-angebot-list-item',
  imports: [
    CommonModule,
    ScrollToAndFocusDirective,
    I18nPluralPipe,
    NgOptimizedImage,
    RouterLink,
    TruncatePipe,
    TerminTabelleKachelComponent,
    FavoriteToggleComponent
  ],
  templateUrl: './angebot-list-item.component.html',
  styleUrl: './angebot-list-item.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class AngebotListItemComponent implements OnInit {
  private readonly sanitizerService = inject(SanitizerService);

  private readonly announcer = inject(LiveAnnouncer);

  private readonly commonState = inject(CommonStateService);

  public isNowWbSuche = inject(LIB_CONFIG).isNowWbSuche;

  @Input({ required: true }) angebot!: Bildungsangebot;

  @Input() expanded = false;

  @Input({ required: true }) selected = false;

  @Output() terminSelected = new EventEmitter<string>();

  @Output() expandedToggled = new EventEmitter<void>();

  @ViewChild('heading')
  private readonly headingElement!: ElementRef<HTMLElement>;

  public anzahlTerminePlural = { '=1': '1 Termin', other: '# Termine' };

  public breakTitle = breakTitle;

  public hasImgError = false;

  private inhaltQuelle?: string;

  private inhaltGewandelt = '';

  public shouldScrollToAnchor = false;

  public ngOnInit(): void {
    this.shouldScrollToAnchor = this.commonState.consumeAnchorScroll(this.angebot.id);
  }

  // Der Getter wird je Änderungsprüfung dreimal gelesen, deshalb wird nur umgewandelt wenn sich das Quellfeld geändert hat.
  get inhaltText(): string {
    if (this.angebot.inhalt !== this.inhaltQuelle) {
      this.inhaltQuelle = this.angebot.inhalt;
      this.inhaltGewandelt = htmlZuText(this.angebot.inhalt);
    }
    return this.inhaltGewandelt;
  }

  public inhaltUmschalten(): void {
    if (!this.expanded) {
      this.announcer.announce(
        `Weiterbildungsinhalte und Termintabelle eingeblendet, ${this.terminAnsage()}.`
      );
    }
    this.expandedToggled.emit();
  }

  public goToHeading(): void {
    this.headingElement?.nativeElement.scrollIntoView();
    this.headingElement?.nativeElement.focus({ preventScroll: true });
  }

  private terminAnsage(): string {
    if (this.angebot.anzahlTermine === 0) {
      return 'keine Termine';
    }
    return this.angebot.anzahlTermine === 1 ? '1 Termin' : `${this.angebot.anzahlTermine} Termine`;
  }

  public sanitizeFunction(html?: string): string {
    return this.sanitizerService.sanitize(html);
  }

  get favoriteEntity(): Weiterbildungsangebot {
    return {
      basisdienst: 'WEITERBILDUNGSANGEBOT',
      favoritTitle: this.angebot.titel,
      targetId: String(this.angebot.id),
      detailsAnbieter: this.angebot.bildungsanbieter?.name
    };
  }

  get anbieterLogo(): string {
    return this.hasImgError
      ? ANBIETER_LOGO_FALLBACK
      : this.angebot.bildungsanbieter?.logo || ANBIETER_LOGO_FALLBACK;
  }

  get weiterbildungsart(): string {
    return getLabelFromWeiterbildungsart(this.angebot.weiterbildungsart);
  }
}
